/**
 * Formatage localisé.
 *
 * Toutes les dates arrivent de l'API en ISO (UTC pour les horodatages, date
 * simple pour les échéances). L'affichage se fait en français, dans le fuseau
 * configuré sur le profil — par défaut Europe/Paris.
 *
 * Les constantes de locale et de fuseau sont centralisées ici : une
 * internationalisation future n'aura pas à parcourir toute l'interface.
 */

export const DEFAULT_LOCALE = "fr-FR";
export const DEFAULT_TIMEZONE = "Europe/Paris";

let locale = DEFAULT_LOCALE;
let timeZone = DEFAULT_TIMEZONE;

export function configureFormatting(options: {
  locale?: string | null;
  timeZone?: string | null;
}): void {
  if (options.locale) locale = options.locale;
  if (options.timeZone) timeZone = options.timeZone;
}

/** Parse une date ISO. Une date nue (`AAAA-MM-JJ`) est lue en heure locale. */
function parseIso(value: string): Date | null {
  if (!value) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const parsed = dateOnly ? new Date(`${value}T12:00:00`) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = parseIso(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatShortDate(value?: string | null): string {
  if (!value) return "—";
  const date = parseIso(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = parseIso(value.endsWith("Z") ? value : `${value}Z`);
  if (!date) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(date);
}

export function formatAmount(
  cents?: number | null,
  currency = "EUR",
): string {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(cents / 100);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Texte d'échéance explicite : « dans 4 jours », « en retard de 2 jours ». */
export function formatDeadlineDistance(days?: number | null): string {
  if (days === null || days === undefined) return "Sans échéance connue";
  if (days < -1) return `En retard de ${Math.abs(days)} jours`;
  if (days === -1) return "En retard d'un jour";
  if (days === 0) return "Échéance aujourd'hui";
  if (days === 1) return "Échéance demain";
  if (days <= 31) return `Dans ${days} jours`;
  const months = Math.round(days / 30);
  return months <= 1 ? "Dans un mois environ" : `Dans ${months} mois environ`;
}

/**
 * Même distance, pour un événement attendu d'un organisme.
 *
 * « En retard » est faux ici : l'utilisateur n'a rien à réaliser, et le délai
 * dépassé est celui d'un tiers. La formulation reste neutre.
 */
export function formatAwaitedDistance(days?: number | null): string {
  if (days === null || days === undefined) return "Aucun délai annoncé";
  if (days < -1) return `Délai écoulé depuis ${Math.abs(days)} jours`;
  if (days === -1) return "Délai écoulé depuis un jour";
  if (days === 0) return "Fin du délai indicatif aujourd'hui";
  if (days === 1) return "Fin du délai indicatif demain";
  if (days <= 31) return `Encore ${days} jours`;
  const months = Math.round(days / 30);
  return months <= 1 ? "Encore un mois environ" : `Encore ${months} mois environ`;
}

/** Montant saisi en euros vers des centimes (évite les erreurs de virgule). */
export function eurosToCents(value: string): number | null {
  const normalized = value.replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

export function centsToEuros(cents?: number | null): string {
  if (cents === null || cents === undefined) return "";
  return (cents / 100).toFixed(2).replace(".", ",");
}

/** Date du jour au format `AAAA-MM-JJ`, dans le fuseau d'affichage. */
export function todayIso(): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  });
  return formatter.format(now);
}

export function pluralize(count: number, singular: string, plural?: string): string {
  const word = count > 1 ? (plural ?? `${singular}s`) : singular;
  return `${count} ${word}`;
}

/** Résumé lisible d'un agent utilisateur, pour la liste des sessions. */
export function describeUserAgent(userAgent?: string | null): string {
  if (!userAgent) return "Appareil inconnu";
  const browser =
    /Edg\//.test(userAgent)
      ? "Edge"
      : /OPR\//.test(userAgent)
        ? "Opera"
        : /Chrome\//.test(userAgent)
          ? "Chrome"
          : /Safari\//.test(userAgent) && !/Chrome/.test(userAgent)
            ? "Safari"
            : /Firefox\//.test(userAgent)
              ? "Firefox"
              : "Navigateur";
  const platform =
    /Windows/.test(userAgent)
      ? "Windows"
      : /Macintosh|Mac OS/.test(userAgent)
        ? "macOS"
        : /Android/.test(userAgent)
          ? "Android"
          : /iPhone|iPad/.test(userAgent)
            ? "iOS"
            : /Linux/.test(userAgent)
              ? "Linux"
              : "";
  return platform ? `${browser} sur ${platform}` : browser;
}
